import { useEffect, useRef, useState, Fragment } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import StatusPill from "@/components/StatusPill";
import { Field, SummaryBar, DataTable } from "@/pages/FridgeLogs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Plus, Trash2, Pencil, Upload, Download, Layers, X, ChevronDown, AlertTriangle, Search } from "lucide-react";
import { toast } from "sonner";

const empty = {
  product_name: "",
  batch_number: "",
  supplier: "",
  quantity: 0,
  unit: "kg",
  manufacture_date: "",
  expiry_date: "",
  ingredients: [],
  notes: "",
};

function bucket(expiry) {
  if (!expiry) return "neutral";
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const ex = new Date(expiry); ex.setHours(0, 0, 0, 0);
  const days = Math.round((ex - today) / 86400000);
  if (days < 0) return "expired";
  if (days <= 7) return "soon";
  return "ok";
}

function earliestExpiry(ingredients = []) {
  const dates = ingredients.map((i) => (i.expiry_date || "").trim()).filter(Boolean);
  if (dates.length === 0) return "";
  return dates.reduce((a, b) => (a < b ? a : b));
}

const CSV_TEMPLATE =
  "product_name,batch_number,supplier,quantity,unit,manufacture_date,expiry_date,notes\n" +
  "Sourdough Starter,SD-001,Bakers Mill,2.5,kg,2026-02-01,2026-02-28,Friday delivery\n" +
  "Organic Tomatoes,TM-2026-04,Green Acres,10,kg,2026-02-15,2026-03-05,\n";

function splitRow(line) {
  const out = []; let cur = ""; let inQ = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (ch === '"') { inQ = !inQ; continue; }
    if (ch === "," && !inQ) { out.push(cur); cur = ""; continue; }
    cur += ch;
  }
  out.push(cur);
  return out;
}

function parseCsv(text) {
  const lines = text.replace(/\r\n?/g, "\n").split("\n").filter((l) => l.trim() !== "");
  if (lines.length === 0) return [];
  const header = splitRow(lines[0]).map((h) => h.trim().toLowerCase());
  return lines.slice(1).map((line) => {
    const cells = splitRow(line);
    const row = {};
    header.forEach((h, i) => (row[h] = (cells[i] ?? "").trim()));
    return row;
  });
}

export default function Batches() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [importPreview, setImportPreview] = useState([]);
  const [importErrors, setImportErrors] = useState([]);
  const [expandedId, setExpandedId] = useState(null);
  const [traceOpen, setTraceOpen] = useState(false);
  const [traceQuery, setTraceQuery] = useState({ name: "", batch_number: "", source_product: "" });
  const fileRef = useRef(null);

  const load = async () => setItems((await api.get("/batches")).data);
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditingId(null); setForm(empty); setOpen(true); };
  const openEdit = (b) => {
    setEditingId(b.id);
    setForm({
      product_name: b.product_name,
      batch_number: b.batch_number,
      supplier: b.supplier || "",
      quantity: b.quantity,
      unit: b.unit,
      manufacture_date: b.manufacture_date || "",
      expiry_date: b.expiry_date,
      ingredients: Array.isArray(b.ingredients) ? b.ingredients : [],
      notes: b.notes || "",
    });
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const cleanedIngredients = (form.ingredients || [])
        .map((i) => ({
          name: (i.name || "").trim(),
          expiry_date: (i.expiry_date || "").trim(),
          batch_number: (i.batch_number || "").trim(),
          supplier: (i.supplier || "").trim(),
          notes: (i.notes || "").trim(),
        }))
        .filter((i) => i.name && i.expiry_date);
      const body = {
        ...form,
        quantity: Number(form.quantity),
        manufacture_date: form.manufacture_date || null,
        ingredients: cleanedIngredients,
      };
      if (editingId) {
        await api.put(`/batches/${editingId}`, body);
        toast.success("Batch updated");
      } else {
        await api.post("/batches", body);
        toast.success("Batch added");
      }
      setOpen(false);
      setEditingId(null);
      setForm(empty);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
    } finally { setBusy(false); }
  };

  const del = async (id) => { await api.delete(`/batches/${id}`); toast.success("Batch removed"); load(); };

  // ---- CSV import
  const onFileChosen = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const text = await file.text();
    e.target.value = "";
    const rows = parseCsv(text);
    if (rows.length === 0) { toast.error("CSV is empty"); return; }
    const errors = [];
    const valid = [];
    rows.forEach((r, idx) => {
      const lineNo = idx + 2;
      if (!r.product_name) errors.push(`Row ${lineNo}: missing product_name`);
      if (!r.batch_number) errors.push(`Row ${lineNo}: missing batch_number`);
      if (!r.expiry_date) errors.push(`Row ${lineNo}: missing expiry_date`);
      if (r.product_name && r.batch_number && r.expiry_date) {
        valid.push({
          product_name: r.product_name,
          batch_number: r.batch_number,
          supplier: r.supplier || "",
          quantity: Number(r.quantity) || 0,
          unit: r.unit || "kg",
          manufacture_date: r.manufacture_date || null,
          expiry_date: r.expiry_date,
          notes: r.notes || "",
        });
      }
    });
    setImportPreview(valid);
    setImportErrors(errors);
    setImportOpen(true);
  };

  const confirmImport = async () => {
    if (importPreview.length === 0) { toast.error("Nothing to import"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/batches/bulk", importPreview);
      toast.success(`Imported ${data.created} batch${data.created === 1 ? "" : "es"}`);
      setImportOpen(false);
      setImportPreview([]);
      setImportErrors([]);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Import failed");
    } finally { setBusy(false); }
  };

  const downloadTemplate = () => {
    const blob = new Blob([CSV_TEMPLATE], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "batch-import-template.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const expired = items.filter((b) => bucket(b.expiry_date) === "expired").length;
  const soon = items.filter((b) => bucket(b.expiry_date) === "soon").length;

  return (
    <div className="p-4 sm:p-8 lg:p-12 max-w-[1400px]" data-testid="batches-page">
      <input
        ref={fileRef}
        type="file"
        accept=".csv,text/csv"
        className="hidden"
        onChange={onFileChosen}
        data-testid="batch-csv-input"
      />

      <PageHeader
        eyebrow="Inventory & traceability"
        title="Batch & expiry"
        description="Track product batches, suppliers, and expiry windows across every site."
        actions={
          <div className="flex items-center gap-2">
            <Button
              data-testid="batch-import-button"
              onClick={() => fileRef.current?.click()}
              className="bg-white hover:bg-[#efece6] text-[#1c1917] border border-[#e5e2d9] rounded-[4px] h-10 px-4"
            >
              <Upload className="w-4 h-4 mr-2" /> Import CSV
            </Button>
            <Button data-testid="batch-add-button" onClick={openCreate} className="bg-[#1b3b36] hover:bg-[#2c5a53] text-white rounded-[4px] h-10 px-5">
              <Plus className="w-4 h-4 mr-2" /> Add batch
            </Button>
          </div>
        }
      />

      <SummaryBar items={[
        { label: "Batches tracked", value: items.length, testid: "stat-total-batches" },
        { label: "Expiring ≤ 7 days", value: soon, tone: soon ? "warning" : "success", testid: "stat-soon" },
        { label: "Expired", value: expired, tone: expired ? "alert" : "success", testid: "stat-expired" },
      ]} />

      <DataTable
        empty="No batches in inventory. Add the first product batch or import a CSV."
        head={["Product", "Batch", "Supplier", "Qty", "Manufactured", "Expires", "Status", ""]}
        rows={items}
        testid="batches-table"
        render={(b, idx) => {
          const ings = Array.isArray(b.ingredients) ? b.ingredients : [];
          const earliest = earliestExpiry(ings);
          const earliestBeforeBatch = earliest && earliest < (b.expiry_date || "");
          const status = bucket(b.expiry_date);
          const isExpanded = expandedId === b.id;
          return (
            <Fragment key={b.id}>
              <tr data-testid={`batch-row-${idx}`} className="border-t border-[#e5e2d9] hover:bg-[#efece6]/40 align-top">
                <td className="px-4 py-3 text-sm">
                  <div className="font-medium">{b.product_name}</div>
                  {ings.length > 0 && (
                    <button
                      type="button"
                      data-testid={`batch-ingredients-toggle-${idx}`}
                      onClick={() => setExpandedId(isExpanded ? null : b.id)}
                      className="inline-flex items-center gap-1 mt-1 text-[10px] uppercase tracking-[0.18em] text-[#1b3b36] hover:underline"
                    >
                      <Layers className="w-3 h-3" />
                      {ings.length} ingredient{ings.length === 1 ? "" : "s"}
                      <ChevronDown className={`w-3 h-3 transition-transform ${isExpanded ? "rotate-180" : ""}`} />
                    </button>
                  )}
                  {b.notes && <div className="text-xs text-[#8a8476] mt-0.5 line-clamp-1">{b.notes}</div>}
                </td>
                <td className="px-4 py-3 text-sm font-mono">{b.batch_number}</td>
                <td className="px-4 py-3 text-sm text-[#57534e]">{b.supplier || "—"}</td>
                <td className="px-4 py-3 text-sm font-mono">{b.quantity} {b.unit}</td>
                <td className="px-4 py-3 text-sm whitespace-nowrap">{b.manufacture_date || "—"}</td>
                <td className="px-4 py-3 text-sm whitespace-nowrap">
                  <div>{b.expiry_date}</div>
                  {earliestBeforeBatch && (
                    <div className="text-[10px] text-[#b9531c] inline-flex items-center gap-1 mt-0.5" data-testid={`batch-earliest-warn-${idx}`}>
                      <AlertTriangle className="w-3 h-3" /> ingredient expires {earliest}
                    </div>
                  )}
                </td>
                <td className="px-4 py-3">
                  <StatusPill tone={status === "expired" ? "alert" : status === "soon" ? "warning" : "success"}>
                    {status === "expired" ? "expired" : status === "soon" ? "soon" : "ok"}
                  </StatusPill>
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="inline-flex items-center gap-3">
                    <button data-testid={`batch-edit-${idx}`} onClick={() => openEdit(b)} className="text-[#8a8476] hover:text-[#1b3b36]" aria-label="Edit"><Pencil className="w-4 h-4" /></button>
                    <button data-testid={`batch-delete-${idx}`} onClick={() => del(b.id)} className="text-[#8a8476] hover:text-[#d94f3d]" aria-label="Delete"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </td>
              </tr>
              {isExpanded && ings.length > 0 && (
                <tr key={`${b.id}-ings`} data-testid={`batch-ingredients-${idx}`} className="bg-[#fbfaf6] border-t border-[#efece6]">
                  <td colSpan={8} className="px-4 py-3">
                    <div className="text-[10px] uppercase tracking-[0.22em] text-[#8a8476] mb-2">Ingredients (sorted by soonest expiry)</div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {[...ings]
                        .sort((a, c) => (a.expiry_date || "").localeCompare(c.expiry_date || ""))
                        .map((ing, j) => {
                          const ingStatus = bucket(ing.expiry_date);
                          const canTrace = !!(ing.name && ing.batch_number);
                          // Composite stable key — sorted lists must not use raw index or React
                          // mixes up state (e.g. the "trace" button) when expiry-date order shifts.
                          const ingKey = `${ing.name || ""}|${ing.batch_number || ""}|${ing.expiry_date || ""}|${j}`;
                          return (
                            <div key={ingKey} data-testid={`batch-ingredient-${idx}-${j}`} className="flex items-start gap-3 border border-[#efece6] bg-white px-3 py-2 text-xs">
                              <div className="flex-1 min-w-0">
                                <div className="text-sm font-medium text-[#1c1917]">{ing.name}</div>
                                <div className="text-[10px] text-[#8a8476] mt-0.5 font-mono uppercase tracking-[0.14em]">
                                  {ing.supplier || "—"}
                                  {ing.batch_number && ` · ${ing.batch_number}`}
                                </div>
                                {ing.notes && <div className="text-xs text-[#57534e] mt-1 line-clamp-2">{ing.notes}</div>}
                                {canTrace && (
                                  <button
                                    type="button"
                                    data-testid={`batch-ingredient-trace-${idx}-${j}`}
                                    onClick={() => {
                                      setTraceQuery({
                                        name: ing.name,
                                        batch_number: ing.batch_number,
                                        source_product: b.product_name,
                                      });
                                      setTraceOpen(true);
                                    }}
                                    className="mt-2 inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] text-[#1b3b36] hover:underline"
                                    title="Find every batch that used this ingredient + batch number"
                                  >
                                    <Search className="w-3 h-3" /> Trace this
                                  </button>
                                )}
                              </div>
                              <div className="text-right shrink-0">
                                <div className="text-xs font-mono">{ing.expiry_date}</div>
                                <StatusPill tone={ingStatus === "expired" ? "alert" : ingStatus === "soon" ? "warning" : "success"}>
                                  {ingStatus === "expired" ? "expired" : ingStatus === "soon" ? "soon" : "ok"}
                                </StatusPill>
                              </div>
                            </div>
                          );
                        })}
                    </div>
                  </td>
                </tr>
              )}
            </Fragment>
          );
        }}
      />

      {/* Create / Edit dialog */}
      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditingId(null); setForm(empty); } }}>
        <DialogContent className="bg-white max-w-2xl max-h-[88vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">{editingId ? "Edit batch" : "New batch"}</DialogTitle>
            <DialogDescription className="sr-only">Track product batches and per-ingredient expiry dates.</DialogDescription>
          </DialogHeader>
          <form className="space-y-4" onSubmit={submit} data-testid="batch-form">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Product"><Input required data-testid="batch-product-input" value={form.product_name} onChange={(e) => setForm((f) => ({ ...f, product_name: e.target.value }))} className="bg-white rounded-[4px]" /></Field>
              <Field label="Batch #"><Input required data-testid="batch-number-input" value={form.batch_number} onChange={(e) => setForm((f) => ({ ...f, batch_number: e.target.value }))} className="bg-white rounded-[4px] font-mono" /></Field>
            </div>
            <Field label="Supplier"><Input data-testid="batch-supplier-input" value={form.supplier} onChange={(e) => setForm((f) => ({ ...f, supplier: e.target.value }))} className="bg-white rounded-[4px]" /></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Quantity"><Input type="number" step="0.01" data-testid="batch-qty-input" value={form.quantity} onChange={(e) => setForm((f) => ({ ...f, quantity: e.target.value }))} className="bg-white rounded-[4px]" /></Field>
              <Field label="Unit"><Input data-testid="batch-unit-input" value={form.unit} onChange={(e) => setForm((f) => ({ ...f, unit: e.target.value }))} className="bg-white rounded-[4px]" /></Field>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Manufactured"><Input type="date" data-testid="batch-mfg-input" value={form.manufacture_date} onChange={(e) => setForm((f) => ({ ...f, manufacture_date: e.target.value }))} className="bg-white rounded-[4px]" /></Field>
              <Field label="Product expires">
                <Input type="date" required data-testid="batch-expiry-input" value={form.expiry_date} onChange={(e) => setForm((f) => ({ ...f, expiry_date: e.target.value }))} className="bg-white rounded-[4px]" />
                {(() => {
                  const e = earliestExpiry(form.ingredients);
                  if (!e || e === form.expiry_date) return null;
                  const earlierThanForm = !form.expiry_date || e < form.expiry_date;
                  return (
                    <button
                      type="button"
                      data-testid="batch-use-earliest"
                      onClick={() => setForm((f) => ({ ...f, expiry_date: e }))}
                      className={`mt-1 text-[10px] uppercase tracking-[0.18em] hover:underline ${earlierThanForm ? "text-[#b9531c]" : "text-[#8a8476]"}`}
                    >
                      {earlierThanForm ? "↓ ingredient expires" : "use"} {e}
                    </button>
                  );
                })()}
              </Field>
            </div>

            <IngredientsEditor
              value={form.ingredients}
              onChange={(next) => setForm((f) => ({ ...f, ingredients: next }))}
            />

            <Field label="Notes"><Textarea value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} className="bg-white rounded-[4px]" /></Field>
            <DialogFooter>
              <Button type="submit" disabled={busy} data-testid="batch-submit-button" className="bg-[#1b3b36] hover:bg-[#2c5a53] text-white rounded-[4px]">
                {busy ? "Saving…" : editingId ? "Save changes" : "Save batch"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* CSV import preview dialog */}
      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent className="bg-white max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="batch-import-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl">Review batch import</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="flex items-center justify-between text-sm">
              <div>
                <span className="text-[#1b3b36] font-medium">{importPreview.length}</span>
                <span className="text-[#8a8476]"> valid batches ready to import</span>
                {importErrors.length > 0 && (
                  <span className="text-[#d94f3d] ml-3">• {importErrors.length} error{importErrors.length === 1 ? "" : "s"}</span>
                )}
              </div>
              <button onClick={downloadTemplate} data-testid="batch-template-download" className="text-xs text-[#1b3b36] inline-flex items-center gap-1 hover:underline">
                <Download className="w-3 h-3" /> Download template
              </button>
            </div>

            {importErrors.length > 0 && (
              <div className="bg-[#fbeae8] border border-[#d94f3d]/30 rounded-[4px] p-3 max-h-32 overflow-y-auto" data-testid="batch-import-errors">
                {importErrors.map((e, i) => (<div key={i} className="text-xs text-[#d94f3d] font-mono">{e}</div>))}
              </div>
            )}

            {importPreview.length > 0 && (
              <div className="border border-[#e5e2d9] overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-[#efece6]">
                      {["Product", "Batch", "Supplier", "Qty", "Expires"].map((h) => (
                        <th key={h} className="px-3 py-2 text-left text-[10px] uppercase tracking-[0.22em] text-[#8a8476] font-medium">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {importPreview.map((r, i) => (
                      <tr key={i} className="border-t border-[#e5e2d9]" data-testid={`batch-import-preview-${i}`}>
                        <td className="px-3 py-2">{r.product_name}</td>
                        <td className="px-3 py-2 font-mono">{r.batch_number}</td>
                        <td className="px-3 py-2">{r.supplier || "—"}</td>
                        <td className="px-3 py-2 font-mono">{r.quantity} {r.unit}</td>
                        <td className="px-3 py-2 whitespace-nowrap">{r.expiry_date}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div className="flex justify-end gap-2">
              <Button type="button" onClick={() => { setImportOpen(false); setImportPreview([]); setImportErrors([]); }} className="bg-[#efece6] hover:bg-[#e5e2d9] text-[#1c1917] rounded-[4px]">
                Cancel
              </Button>
              <Button type="button" disabled={busy || importPreview.length === 0} onClick={confirmImport} data-testid="batch-import-confirm" className="bg-[#1b3b36] hover:bg-[#2c5a53] text-white rounded-[4px]">
                {busy ? "Importing…" : `Import ${importPreview.length} batch${importPreview.length === 1 ? "" : "es"}`}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <TraceDialog open={traceOpen} onOpenChange={setTraceOpen} query={traceQuery} />
    </div>
  );
}

function TraceDialog({ open, onOpenChange, query }) {
  const [results, setResults] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || !query?.name || !query?.batch_number) { setResults(null); return; }
    setBusy(true);
    api
      .get(`/batches/trace?name=${encodeURIComponent(query.name)}&batch_number=${encodeURIComponent(query.batch_number)}`)
      .then((r) => setResults(r.data))
      .catch(() => setResults({ match_count: 0, matches: [] }))
      .finally(() => setBusy(false));
  }, [open, query]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="batch-trace-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl inline-flex items-center gap-2">
            <Search className="w-5 h-5 text-[#1b3b36]" /> Trace ingredient
          </DialogTitle>
          <DialogDescription className="text-sm text-[#57534e]">
            Every batch that used <span className="font-medium text-[#1c1917]">{query?.name || "—"}</span>
            {" "}from batch <span className="font-mono text-[#1c1917]">{query?.batch_number || "—"}</span>.
            Use this during a supplier recall to identify every product that needs to be pulled.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3" data-testid="batch-trace-results">
          {busy && (
            <div className="py-6 text-center text-xs text-[#8a8476]">Searching…</div>
          )}
          {!busy && results && (
            <>
              <div className="flex items-center justify-between text-xs">
                <span className="text-[#8a8476]">Match count</span>
                <span className="font-mono text-[#1b3b36] text-lg" data-testid="batch-trace-count">
                  {results.match_count}
                </span>
              </div>
              {results.match_count === 0 && (
                <div className="py-8 text-center text-sm text-[#4e7b5b] border border-[#e5e2d9] bg-[#fbfaf6]" data-testid="batch-trace-empty">
                  No matching batches. Either the recall doesn&apos;t affect any current stock — or this ingredient was never logged with that batch number.
                </div>
              )}
              {results.match_count > 0 && (
                <div className="border border-[#e5e2d9] divide-y divide-[#efece6]">
                  {results.matches.map((m, idx) => {
                    const st = bucket(m.expiry_date);
                    return (
                      <div key={m.batch_id} data-testid={`batch-trace-row-${idx}`} className="px-3 py-3 grid grid-cols-1 md:grid-cols-4 gap-2">
                        <div className="md:col-span-2">
                          <div className="text-sm font-medium text-[#1c1917]">{m.product_name}</div>
                          <div className="text-[10px] uppercase tracking-[0.18em] text-[#8a8476] font-mono mt-0.5">
                            batch {m.batch_number} {m.supplier && `· ${m.supplier}`}
                          </div>
                          {m.ingredient_notes && (
                            <div className="text-[11px] text-[#b9531c] mt-1 italic">Note: {m.ingredient_notes}</div>
                          )}
                        </div>
                        <div className="text-xs text-[#57534e]">
                          <div className="font-mono">{m.quantity} {m.unit}</div>
                          <div className="text-[10px] text-[#8a8476] uppercase tracking-[0.14em] mt-0.5">made {m.manufacture_date || "—"}</div>
                        </div>
                        <div className="text-right">
                          <div className="text-xs font-mono">{m.expiry_date}</div>
                          <StatusPill tone={st === "expired" ? "alert" : st === "soon" ? "warning" : "success"}>
                            {st === "expired" ? "expired" : st === "soon" ? "soon" : "ok"}
                          </StatusPill>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function IngredientsEditor({ value = [], onChange }) {
  const items = value;
  const update = (idx, patch) => onChange(items.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  const add = () => onChange([...items, { name: "", expiry_date: "", batch_number: "", supplier: "", notes: "" }]);
  const remove = (idx) => onChange(items.filter((_, i) => i !== idx));

  return (
    <div className="border-t border-[#e5e2d9] pt-4" data-testid="batch-ingredients-editor">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[11px] uppercase tracking-[0.22em] text-[#8a8476] inline-flex items-center gap-2">
          <Layers className="w-3.5 h-3.5" />
          Ingredients & per-item expiry
          {items.length > 0 && (
            <span className="font-mono text-[#1b3b36] normal-case tracking-normal">{items.length}</span>
          )}
        </div>
        <button
          type="button"
          data-testid="batch-ingredient-add"
          onClick={add}
          className="text-xs text-[#1b3b36] inline-flex items-center gap-1 hover:underline"
        >
          <Plus className="w-3 h-3" /> Add ingredient
        </button>
      </div>

      {items.length === 0 && (
        <div className="text-xs text-[#8a8476] italic py-2">
          Optional. Add each ingredient with its own batch & expiry so the product expiry can track the soonest.
        </div>
      )}

      <div className="space-y-2">
        {items.map((it, idx) => (
          <div key={idx} data-testid={`batch-ingredient-row-${idx}`} className="border border-[#e5e2d9] bg-[#fbfaf6] p-3 space-y-2">
            <div className="grid grid-cols-1 md:grid-cols-12 gap-2 items-start">
              <div className="md:col-span-6">
                <IngredientNameInput
                  value={it.name}
                  onChange={(v) => update(idx, { name: v })}
                  testid={`batch-ingredient-name-${idx}`}
                />
              </div>
              <div className="md:col-span-4">
                <Input
                  type="date"
                  required={!!it.name}
                  data-testid={`batch-ingredient-expiry-${idx}`}
                  value={it.expiry_date}
                  onChange={(e) => update(idx, { expiry_date: e.target.value })}
                  className="bg-white rounded-[3px] h-9 text-sm"
                  placeholder="Expires"
                />
              </div>
              <div className="md:col-span-2 flex items-center justify-end">
                <button
                  type="button"
                  onClick={() => remove(idx)}
                  data-testid={`batch-ingredient-remove-${idx}`}
                  className="text-[#8a8476] hover:text-[#d94f3d]"
                  aria-label="Remove ingredient"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <Input
                data-testid={`batch-ingredient-supplier-${idx}`}
                value={it.supplier || ""}
                onChange={(e) => update(idx, { supplier: e.target.value })}
                placeholder="Supplier (optional)"
                className="bg-white rounded-[3px] h-9 text-sm"
              />
              <Input
                data-testid={`batch-ingredient-batch-${idx}`}
                value={it.batch_number || ""}
                onChange={(e) => update(idx, { batch_number: e.target.value })}
                placeholder="Ingredient batch # (optional)"
                className="bg-white rounded-[3px] h-9 text-sm font-mono"
              />
            </div>
            <Input
              data-testid={`batch-ingredient-notes-${idx}`}
              value={it.notes || ""}
              onChange={(e) => update(idx, { notes: e.target.value })}
              placeholder="Notes (e.g. opened pack, refrigerated post-receipt)"
              className="bg-white rounded-[3px] h-9 text-sm"
            />
          </div>
        ))}
      </div>
    </div>
  );
}

function IngredientNameInput({ value, onChange, testid }) {
  const [open, setOpen] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [highlight, setHighlight] = useState(0);
  const wrapRef = useRef(null);
  const debounced = useRef(null);

  useEffect(() => {
    if (debounced.current) clearTimeout(debounced.current);
    if (!value || value.trim().length < 1) {
      setSuggestions([]);
      return;
    }
    debounced.current = setTimeout(async () => {
      try {
        const { data } = await api.get(`/batches/ingredient-suggestions?q=${encodeURIComponent(value.trim())}`);
        const filtered = data.filter((d) => d.name.toLowerCase() !== value.trim().toLowerCase());
        setSuggestions(filtered);
      } catch {
        setSuggestions([]);
      }
    }, 180);
    return () => clearTimeout(debounced.current);
  }, [value]);

  useEffect(() => {
    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const pick = (name) => {
    onChange(name);
    setOpen(false);
  };

  const onKeyDown = (e) => {
    if (!open || suggestions.length === 0) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setHighlight((h) => (h + 1) % suggestions.length); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setHighlight((h) => (h - 1 + suggestions.length) % suggestions.length); }
    else if (e.key === "Enter") {
      if (highlight >= 0 && highlight < suggestions.length) {
        e.preventDefault();
        pick(suggestions[highlight].name);
      }
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <div ref={wrapRef} className="relative">
      <Input
        data-testid={testid}
        value={value}
        onChange={(e) => { onChange(e.target.value); setOpen(true); setHighlight(0); }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
        placeholder="Ingredient name (start typing to see past entries)"
        className="bg-white rounded-[3px] h-9 text-sm"
        autoComplete="off"
      />
      {open && suggestions.length > 0 && (
        <div
          data-testid={`${testid}-dropdown`}
          className="absolute z-30 mt-1 left-0 right-0 max-h-56 overflow-y-auto bg-white border border-[#e5e2d9] shadow-lg"
        >
          {suggestions.map((s, i) => (
            <button
              key={s.name}
              type="button"
              data-testid={`${testid}-option-${i}`}
              onMouseDown={(e) => { e.preventDefault(); pick(s.name); }}
              onMouseEnter={() => setHighlight(i)}
              className={`w-full text-left px-3 py-2 text-sm flex items-center justify-between gap-3 ${i === highlight ? "bg-[#efece6]" : "bg-white"}`}
            >
              <span className="truncate">{s.name}</span>
              <span className="text-[10px] uppercase tracking-[0.18em] text-[#8a8476]">used {s.count}×</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
